import { Controller } from "@hotwired/stimulus"

// Custom datepicker controller — no external dependencies.
// Works with a hidden input (ISO date) and a display input (MM/DD/YYYY).
//
// Expected markup:
//   <div data-controller="datepicker"
//        data-datepicker-min-value="1920-01-01"
//        data-datepicker-max-value="2026-04-04">
//     <input type="hidden" data-datepicker-target="hidden" name="..." value="1990-05-15" />
//     <input type="text"   data-datepicker-target="display" placeholder="MM/DD/YYYY" />
//     <button type="button" data-datepicker-target="icon" aria-label="Open calendar">
//       <i class="fa-solid fa-calendar"></i>
//     </button>
//   </div>
export default class extends Controller {
  static targets = ["display", "hidden", "icon"]
  static values = {
    min: { type: String, default: "1920-01-01" },
    max: { type: String, default: "" },
  }

  connect() {
    this.isOpen = false
    this.popup = null
    this.focusedDate = null

    // If max is empty, default to today
    if (!this.maxValue) {
      this.maxValue = this._isoToday()
    }

    // Initialize viewed month from current value or today
    const initial = this._parseISO(this.hiddenTarget.value) || new Date()
    this.viewedYear = initial.getFullYear()
    this.viewedMonth = initial.getMonth()

    // Bind external click handler so we can remove it later
    this._onOutsideClick = this._handleOutsideClick.bind(this)
    this._onKeydown = this._handleKeydown.bind(this)

    // Event listeners on targets
    this.displayTarget.addEventListener("click", () => this.open())
    this.displayTarget.addEventListener("input", () => this._onDisplayInput())
    this.displayTarget.addEventListener("blur", () => this._onDisplayBlur())
    this.displayTarget.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault()
        this._onDisplayBlur()
        this.close()
      }
    })
    this.iconTarget.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggle()
    })
  }

  disconnect() {
    this.close()
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true

    // Sync viewed month to current value
    const current = this._parseISO(this.hiddenTarget.value)
    if (current) {
      this.viewedYear = current.getFullYear()
      this.viewedMonth = current.getMonth()
      this.focusedDate = new Date(current)
    } else {
      const today = new Date()
      this.viewedYear = today.getFullYear()
      this.viewedMonth = today.getMonth()
      this.focusedDate = new Date(today)
    }

    this._buildPopup()
    this._positionPopup()
    document.addEventListener("click", this._onOutsideClick, true)
    document.addEventListener("keydown", this._onKeydown, true)
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    if (this.popup) {
      this.popup.remove()
      this.popup = null
    }
    document.removeEventListener("click", this._onOutsideClick, true)
    document.removeEventListener("keydown", this._onKeydown, true)
  }

  // ── Popup Construction ──────────────────────────────────────────

  _buildPopup() {
    if (this.popup) this.popup.remove()

    const container = document.createElement("div")
    container.className =
      "absolute bg-white border border-neutral-200 rounded-xl shadow-lg z-50 p-3 w-[280px]"
    container.setAttribute("role", "dialog")
    container.setAttribute("aria-label", "Choose date")

    container.innerHTML = this._headerHTML() + this._gridHTML()
    this.element.appendChild(container)
    this.popup = container

    // Attach listener once on the container — survives innerHTML rebuilds
    // because we use event delegation (closest("[data-action]"))
    this._popupClickHandler = this._handlePopupClick.bind(this)
    this.popup.addEventListener("click", this._popupClickHandler)
  }

  _headerHTML() {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ]

    const minDate = this._parseISO(this.minValue)
    const maxDate = this._parseISO(this.maxValue)
    const minYear = minDate ? minDate.getFullYear() : 1920
    const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear()

    const monthItems = months
      .map(
        (m, i) =>
          `<button type="button" data-action="pick-month" data-value="${i}"
            class="w-full px-3 py-1.5 text-sm text-left hover:bg-neutral-50 transition-colors cursor-pointer
            ${i === this.viewedMonth ? "text-primary-700 font-medium bg-primary-50/50" : "text-neutral-700"}">${m}</button>`
      )
      .join("")

    let yearItems = ""
    for (let y = maxYear; y >= minYear; y--) {
      yearItems += `<button type="button" data-action="pick-year" data-value="${y}"
        class="w-full px-3 py-1.5 text-sm text-left hover:bg-neutral-50 transition-colors cursor-pointer
        ${y === this.viewedYear ? "text-primary-700 font-medium bg-primary-50/50" : "text-neutral-700"}">${y}</button>`
    }

    return `
      <div class="flex items-center justify-between mb-2">
        <button type="button" data-action="prev-month"
          class="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100" aria-label="Previous month">
          <i class="fa-solid fa-chevron-left text-xs"></i>
        </button>
        <div class="flex gap-1">
          <div class="relative" data-dropdown="month">
            <button type="button" data-action="toggle-dropdown" data-dropdown-target="month"
              class="flex items-center gap-1 text-sm font-medium text-neutral-900 border border-neutral-200 rounded-lg px-2.5 py-1.5 bg-white hover:border-neutral-300 shadow-sm transition-colors cursor-pointer">
              <span data-label="month">${months[this.viewedMonth]}</span>
              <i class="fa-solid fa-chevron-down text-[10px] text-neutral-400"></i>
            </button>
            <div data-menu="month" class="hidden absolute z-50 mt-1 w-36 bg-white border border-neutral-200 rounded-xl shadow-lg max-h-52 overflow-y-auto py-1">
              ${monthItems}
            </div>
          </div>
          <div class="relative" data-dropdown="year">
            <button type="button" data-action="toggle-dropdown" data-dropdown-target="year"
              class="flex items-center gap-1 text-sm font-medium text-neutral-900 border border-neutral-200 rounded-lg px-2.5 py-1.5 bg-white hover:border-neutral-300 shadow-sm transition-colors cursor-pointer">
              <span data-label="year">${this.viewedYear}</span>
              <i class="fa-solid fa-chevron-down text-[10px] text-neutral-400"></i>
            </button>
            <div data-menu="year" class="hidden absolute z-50 mt-1 w-24 bg-white border border-neutral-200 rounded-xl shadow-lg max-h-52 overflow-y-auto py-1">
              ${yearItems}
            </div>
          </div>
        </div>
        <button type="button" data-action="next-month"
          class="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100" aria-label="Next month">
          <i class="fa-solid fa-chevron-right text-xs"></i>
        </button>
      </div>`
  }

  _gridHTML() {
    const weekdays = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]
    const headerCells = weekdays
      .map((d) => `<div class="text-xs font-medium text-neutral-500 text-center">${d}</div>`)
      .join("")

    const cells = this._dayCells()

    return `
      <div class="grid grid-cols-7 gap-0.5 mb-1">${headerCells}</div>
      <div class="grid grid-cols-7 gap-0.5">${cells}</div>`
  }

  _dayCells() {
    const year = this.viewedYear
    const month = this.viewedMonth
    const firstDay = new Date(year, month, 1)
    // Monday-based: 0=Mon … 6=Sun
    let startWeekday = firstDay.getDay() - 1
    if (startWeekday < 0) startWeekday = 6

    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const prevMonthDays = new Date(year, month, 0).getDate()

    const selectedISO = this.hiddenTarget.value
    const todayISO = this._isoToday()
    const minDate = this._parseISO(this.minValue)
    const maxDate = this._parseISO(this.maxValue)

    let cells = ""

    // Previous month trailing days
    for (let i = startWeekday - 1; i >= 0; i--) {
      const day = prevMonthDays - i
      const d = new Date(year, month - 1, day)
      const iso = this._toISO(d)
      const disabled = this._isOutOfRange(d, minDate, maxDate)
      cells += this._dayCell(day, iso, false, disabled, iso === selectedISO, iso === todayISO)
    }

    // Current month
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(year, month, day)
      const iso = this._toISO(d)
      const disabled = this._isOutOfRange(d, minDate, maxDate)
      cells += this._dayCell(day, iso, true, disabled, iso === selectedISO, iso === todayISO)
    }

    // Next month leading days
    const totalCells = startWeekday + daysInMonth
    const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7)
    for (let day = 1; day <= remaining; day++) {
      const d = new Date(year, month + 1, day)
      const iso = this._toISO(d)
      const disabled = this._isOutOfRange(d, minDate, maxDate)
      cells += this._dayCell(day, iso, false, disabled, iso === selectedISO, iso === todayISO)
    }

    return cells
  }

  _dayCell(day, iso, inMonth, disabled, selected, today) {
    let classes =
      "w-9 h-9 flex items-center justify-center text-sm rounded-full"

    if (disabled) {
      classes += " text-neutral-300 cursor-not-allowed"
    } else if (selected) {
      classes += " bg-primary-600 text-white hover:bg-primary-700 cursor-pointer"
    } else if (!inMonth) {
      classes += " text-neutral-400 cursor-pointer hover:bg-neutral-100"
    } else {
      classes += " text-neutral-700 cursor-pointer hover:bg-neutral-100"
    }

    if (today && !selected) {
      classes += " ring-1 ring-primary-500"
    }

    const attrs = disabled
      ? ""
      : `data-action="select-day" data-date="${iso}"`

    return `<button type="button" class="${classes}" ${attrs}>${day}</button>`
  }

  // ── Popup Positioning ───────────────────────────────────────────

  _positionPopup() {
    if (!this.popup) return

    const inputRect = this.displayTarget.getBoundingClientRect()
    const popupHeight = this.popup.offsetHeight
    const spaceBelow = window.innerHeight - inputRect.bottom
    const spaceAbove = inputRect.top

    if (spaceBelow < popupHeight + 8 && spaceAbove > popupHeight + 8) {
      // Open above
      this.popup.style.bottom = `${this.element.offsetHeight + 4}px`
      this.popup.style.top = "auto"
    } else {
      // Open below (default)
      this.popup.style.top = `${this.element.offsetHeight + 4}px`
      this.popup.style.bottom = "auto"
    }
    this.popup.style.left = "0"
  }

  // ── Popup Event Handling (delegated, attached once) ──────────────

  _handlePopupClick(e) {
    const btn = e.target.closest("[data-action]")
    if (!btn) return
    e.preventDefault()
    e.stopPropagation()

    const action = btn.getAttribute("data-action")

    if (action === "select-day") {
      this._selectDate(btn.getAttribute("data-date"))
    } else if (action === "prev-month") {
      this._changeMonth(-1)
    } else if (action === "next-month") {
      this._changeMonth(1)
    } else if (action === "toggle-dropdown") {
      this._toggleHeaderDropdown(btn.getAttribute("data-dropdown-target"))
    } else if (action === "pick-month") {
      this.viewedMonth = parseInt(btn.getAttribute("data-value"), 10)
      this._closeAllHeaderDropdowns()
      this._rebuildGrid()
    } else if (action === "pick-year") {
      this.viewedYear = parseInt(btn.getAttribute("data-value"), 10)
      this._closeAllHeaderDropdowns()
      this._rebuildGrid()
    }
  }

  // ── Header Dropdowns ─────────────────────────────────────────────

  _toggleHeaderDropdown(name) {
    if (!this.popup) return
    const menu = this.popup.querySelector(`[data-menu="${name}"]`)
    if (!menu) return

    // Close the other dropdown first
    const otherName = name === "month" ? "year" : "month"
    const otherMenu = this.popup.querySelector(`[data-menu="${otherName}"]`)
    if (otherMenu) otherMenu.classList.add("hidden")

    menu.classList.toggle("hidden")

    // Scroll selected item into view
    if (!menu.classList.contains("hidden")) {
      const selected = menu.querySelector(".text-primary-700")
      if (selected) selected.scrollIntoView({ block: "nearest" })
    }
  }

  _closeAllHeaderDropdowns() {
    if (!this.popup) return
    this.popup.querySelectorAll("[data-menu]").forEach((m) => m.classList.add("hidden"))
  }

  // ── Actions ─────────────────────────────────────────────────────

  _selectDate(iso) {
    this.hiddenTarget.value = iso
    this.displayTarget.value = this._isoToDisplay(iso)
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.displayTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  _changeMonth(delta) {
    this.viewedMonth += delta
    if (this.viewedMonth > 11) {
      this.viewedMonth = 0
      this.viewedYear++
    } else if (this.viewedMonth < 0) {
      this.viewedMonth = 11
      this.viewedYear--
    }
    this._rebuildGrid()
  }

  _rebuildGrid() {
    if (!this.popup) return
    this.popup.innerHTML = this._headerHTML() + this._gridHTML()
    // No need to re-attach listeners — the click handler is on the
    // popup container using event delegation, so it survives innerHTML replacement.
  }

  // ── Keyboard Navigation ─────────────────────────────────────────

  _handleKeydown(e) {
    if (!this.isOpen) return

    if (e.key === "Escape") {
      e.preventDefault()
      this.close()
      this.displayTarget.focus()
      return
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault()
      this._changeMonth(-1)
    } else if (e.key === "ArrowRight") {
      e.preventDefault()
      this._changeMonth(1)
    }
  }

  // ── Outside Click ───────────────────────────────────────────────

  _handleOutsideClick(e) {
    if (!this.isOpen) return
    if (this.element.contains(e.target)) return
    this.close()
  }

  // ── Display Input Formatting ─────────────────────────────────────

  _onDisplayInput() {
    const input = this.displayTarget
    const cursorPos = input.selectionStart
    const oldValue = input.value

    // Count digits before cursor in old value
    let digitsBefore = 0
    for (let i = 0; i < cursorPos; i++) {
      if (/\d/.test(oldValue[i])) digitsBefore++
    }

    // Extract only digits, cap at 8 (MMDDYYYY)
    const digits = oldValue.replace(/\D/g, "").slice(0, 8)

    // Format as MM/DD/YYYY progressively
    let formatted = ""
    if (digits.length <= 2) {
      formatted = digits
    } else if (digits.length <= 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`
    } else {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
    }

    // Only update if changed (avoids infinite loop)
    if (formatted !== oldValue) {
      input.value = formatted

      // Restore cursor: walk formatted string to place after same digit count
      let newPos = 0
      let counted = 0
      for (let i = 0; i < formatted.length; i++) {
        newPos = i + 1
        if (/\d/.test(formatted[i])) {
          counted++
          if (counted >= digitsBefore) break
        }
      }
      if (digitsBefore === 0) newPos = 0
      input.setSelectionRange(newPos, newPos)
    }

    // If we have a complete date (8 digits), sync to hidden field
    if (digits.length === 8) {
      this._syncTypedDate(digits)
    }
  }

  _syncTypedDate(digits) {
    const month = parseInt(digits.slice(0, 2), 10)
    const day = parseInt(digits.slice(2, 4), 10)
    const year = parseInt(digits.slice(4, 8), 10)

    if (month < 1 || month > 12 || day < 1 || day > 31) return

    const d = new Date(year, month - 1, day)
    if (d.getMonth() !== month - 1 || d.getDate() !== day) return

    const minDate = this._parseISO(this.minValue)
    const maxDate = this._parseISO(this.maxValue)
    if (this._isOutOfRange(d, minDate, maxDate)) return

    this.hiddenTarget.value = this._toISO(d)
  }

  _onDisplayBlur() {
    const raw = this.displayTarget.value.trim()
    if (!raw) {
      this.hiddenTarget.value = ""
      this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    const match = raw.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/)
    if (!match) return // Leave as-is if unparseable

    const month = parseInt(match[1], 10)
    const day = parseInt(match[2], 10)
    const year = parseInt(match[3], 10)

    if (month < 1 || month > 12 || day < 1 || day > 31) return

    const d = new Date(year, month - 1, day)
    // Verify date is valid (not e.g. Feb 31)
    if (d.getMonth() !== month - 1 || d.getDate() !== day) return

    const minDate = this._parseISO(this.minValue)
    const maxDate = this._parseISO(this.maxValue)
    if (this._isOutOfRange(d, minDate, maxDate)) return

    const iso = this._toISO(d)
    this.hiddenTarget.value = iso
    this.displayTarget.value = this._isoToDisplay(iso)
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // ── Date Utilities ──────────────────────────────────────────────

  _parseISO(str) {
    if (!str) return null
    const [y, m, d] = str.split("-").map(Number)
    if (!y || !m || !d) return null
    return new Date(y, m - 1, d)
  }

  _toISO(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  _isoToday() {
    return this._toISO(new Date())
  }

  _isoToDisplay(iso) {
    const [y, m, d] = iso.split("-")
    return `${m}/${d}/${y}`
  }

  _isOutOfRange(date, minDate, maxDate) {
    if (minDate && date < minDate) return true
    if (maxDate && date > maxDate) return true
    return false
  }
}
